/// <reference types="vite/client" />

import {
  ApiHttpError,
  createApiClient,
  DEFAULT_BASE_URL,
  isWebPublicAuthPath,
  isWebRefreshTokenPath,
  WEB_AUTH_BASE_PATH,
  type AuthAdapter,
} from '@crowdfpl/api-client';
import { validateApiConfig } from '@crowdfpl/utils/api-config';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  ACCESS_TOKEN_EXPIRES_AT_KEY,
  CONFIG_BASE_PATH,
} from '@/constants';
import { useAuth } from '@/hooks/auth';
import { RefreshTokenResponseSchema } from '@/types/auth';
import { handleError } from './errorHandler';

export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || DEFAULT_BASE_URL,
} as const;

const webAuthAdapter: AuthAdapter = {
  getAuthHeader(url: string): Record<string, string> {
    const isPublicConfig = url.startsWith(CONFIG_BASE_PATH);
    if (isWebPublicAuthPath(url) || isPublicConfig) return {};

    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  },

  isAuthExempt: isWebRefreshTokenPath,

  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),

  async refreshAccessToken(refreshToken: string) {
    const response = await fetch(`${API_CONFIG.BASE_URL}${WEB_AUTH_BASE_PATH}/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new ApiHttpError('Refresh failed', { status: response.status, isAuthError: true });
    }

    const parsed = RefreshTokenResponseSchema.parse(await response.json());
    return {
      accessToken: parsed.data.accessToken,
      accessTokenExpires: parsed.data.accessTokenExpires,
    };
  },

  persistTokens(tokens) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    if (tokens.accessTokenExpires) {
      localStorage.setItem(ACCESS_TOKEN_EXPIRES_AT_KEY, tokens.accessTokenExpires);
    }
  },

  clearTokens(reason) {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
    try {
      useAuth.getState().setUnverifiedEmail(null);
      if (reason === 'no_refresh_token') {
        useAuth.getState().setUser(null);
      }
    } catch {
      /* noop */
    }
    window.dispatchEvent(new CustomEvent('auth-logout', { detail: { reason } }));
  },

  onTokenRefreshed: () => {
    window.dispatchEvent(new CustomEvent('auth-token-refreshed'));
  },
};

const { apiRequest, api } = createApiClient({
  baseUrl: API_CONFIG.BASE_URL,
  authAdapter: webAuthAdapter,
  reportError: (error, context) => handleError(error, context),
});

export { ApiHttpError, api, apiRequest };

validateApiConfig();
